import React from 'react';
import { CheckCircle2, AlertTriangle, Clock, ChevronRight, ChevronDown } from 'lucide-react';
import { TOKENS } from '../styles';
import type { PD7AStatus } from '../types';

interface Props {
  title: string;
  sub: string;
  status: PD7AStatus;
  dueDate: string;
  amount: string;
  breakdownOpen: boolean;
  onToggleBreakdown: () => void;
  onPayFile: () => void;
}

interface PillMeta {
  bg: string;
  color: string;
  icon: React.ReactNode;
  text: string;
}

function getPillMeta(status: PD7AStatus): PillMeta {
  if (status === 'ready_to_pay') {
    return {
      bg: TOKENS.tealTint,
      color: TOKENS.tealInk,
      icon: <CheckCircle2 size={14} strokeWidth={2.5} />,
      text: 'Ready to pay',
    };
  }
  if (status === 'overdue') {
    return {
      bg: TOKENS.amberTint,
      color: TOKENS.amberText,
      icon: <AlertTriangle size={14} strokeWidth={2.5} />,
      text: 'Overdue',
    };
  }
  return {
    bg: TOKENS.neutralPill,
    color: TOKENS.dark,
    icon: <Clock size={14} strokeWidth={2.5} />,
    text: 'Waiting',
  };
}

export default function ObligationCard({
  title, sub, status, dueDate, amount,
  breakdownOpen, onToggleBreakdown, onPayFile,
}: Props) {
  const pill = getPillMeta(status);

  return (
    <div style={{
      background: TOKENS.card,
      border: '1px solid ' + TOKENS.line,
      borderLeft: '4px solid ' + TOKENS.teal,
      borderRadius: 12,
      padding: '16px 18px',
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 20,
    }}>
      <div style={{ flex: 1, minWidth: 170 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: TOKENS.ink }}>{title}</div>
        <div style={{ fontSize: 13, color: TOKENS.dark, fontWeight: 600 }}>{sub}</div>
        <div>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 12, padding: '4px 10px', borderRadius: 999,
            fontWeight: 700, marginTop: 8,
            background: pill.bg, color: pill.color,
          }}>
            {pill.icon}
            {pill.text}
          </span>
        </div>
      </div>

      <div style={{ marginRight: 8 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: TOKENS.ink }}>Due {dueDate}</div>
        <div style={{ fontSize: 12, color: TOKENS.dark, fontWeight: 600 }}>Pay by {dueDate}</div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 36 }}>
        <button
          onClick={onToggleBreakdown}
          aria-label={breakdownOpen ? 'Hide breakdown' : 'Show breakdown'}
          style={{
            background: 'none', border: 'none', color: TOKENS.dark,
            cursor: 'pointer', padding: 2,
            display: 'inline-flex',
            transition: 'transform 0.15s ease',
            transform: breakdownOpen ? 'rotate(90deg)' : 'rotate(0deg)',
          }}
        >
          <ChevronRight size={16} strokeWidth={2.5} />
        </button>
        <span style={{
          fontSize: 20, fontWeight: 700,
          color: TOKENS.ink, fontVariantNumeric: 'tabular-nums',
        }}>
          {amount}
        </span>
      </div>

      <button
        onClick={onPayFile}
        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = TOKENS.tealHover; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = TOKENS.teal; }}
        style={{
          fontFamily: 'inherit', fontWeight: 700, fontSize: 14,
          borderRadius: 10, padding: '10px 16px', cursor: 'pointer',
          border: '1px solid transparent',
          boxShadow: '0 1px 2px rgba(16, 30, 40, 0.06)',
          display: 'inline-flex', alignItems: 'center', gap: 7,
          background: TOKENS.teal, color: 'white',
          marginLeft: 8,
        }}
      >
        Pay and file
        <ChevronDown size={14} strokeWidth={2.5} />
      </button>
    </div>
  );
}